import React from "react";
import { AlertCircle, History, Lock } from "lucide-react";

export const AuditSkeleton: React.FC<{ rows?: number }> = ({ rows = 4 }) => (
    <div className="space-y-5 px-1" aria-busy="true" aria-label="Loading audit logs">
        {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="flex animate-pulse gap-3">
                <div className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-gray-200" />
                <div className="flex-1 space-y-2">
                    <div className="h-3 w-1/3 rounded bg-gray-200" />
                    <div className="h-2.5 w-2/3 rounded bg-gray-100" />
                    <div className="h-8 w-full rounded bg-gray-100" />
                </div>
            </div>
        ))}
    </div>
);

export const AuditEmptyState: React.FC<{ title?: string; hint?: string }> = ({
    title = "No changes recorded",
    hint = "Try a wider date range or fewer filters.",
}) => (
    <div className="flex min-h-[220px] flex-col items-center justify-center text-center">
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
            <History className="h-5 w-5 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-800">{title}</p>
        <p className="mt-1 text-xs text-gray-500">{hint}</p>
    </div>
);

export const AuditErrorState: React.FC<{ message: string; onRetry?: () => void }> = ({ message, onRetry }) => {
    const forbidden = /privilege|authentication failed|forbidden/i.test(message);
    const Icon = forbidden ? Lock : AlertCircle;
    return (
        <div className="flex min-h-[220px] flex-col items-center justify-center text-center">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
                <Icon className="h-5 w-5 text-red-500" />
            </div>
            <p className="text-sm font-medium text-gray-800">
                {forbidden ? "You don't have access to audit logs" : "Couldn't load audit logs"}
            </p>
            <p className="mt-1 max-w-xs text-xs text-gray-500">{message}</p>
            {onRetry && !forbidden && (
                <button
                    type="button"
                    onClick={onRetry}
                    className="mt-3 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                    Try again
                </button>
            )}
        </div>
    );
};
